import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { AiChatProps, ConnectionInformation, SendMessageOptions } from './AiChatTypes';
import { AiChatServiceContext, type AiChatContextState } from './AiChatContext';
import { EventBusService } from '@lib/services/eventBusService';
import { AgentGatewayWSAsync } from '@lib/services/servers/AgentGatewayWSAsync';
import { LocalStorageService } from '@lib/services/storageService';
import { SessionStateManagerService } from './sessionStateManagerService';
import type { Interaction } from '@lib/models/Interaction';
import { createAiChatSessionStore } from './AiChatSessionStore';
import { ConnectionState } from '@lib/models/connectionState';
import type { INotificationPort } from '@lib/interfaces/INotificationPort';
import { WebSocketConnecionV2 } from '@lib/services/WebSocketConnection/WebSocketConnectionV2';
import type { IWebSocketConnection } from '@lib/services/WebSocketConnection/webSocketConnection';
import { getModelVendor } from '@lib/models/enums';
import { enqueueSnackbar } from 'notistack';
import { HttpSessionService } from '../../services/httpSessionService';
import { HttpArtifactService } from '../../services/httpArtifactService';
import { AiChatState } from '..';
import { useTheme } from '@mui/material/styles';
import { ClientMessage } from '@lib/models/ClientMessage';

interface AiChatProviderProps extends AiChatProps {
    children: ReactNode;
}

export const AiChatProvider: React.FC<AiChatProviderProps> = ({
    agentContext,
    serverBaseUrl,
    user,
    sessionService = new HttpSessionService(serverBaseUrl,user.access_token),
    sessionId,
    artifactService = new HttpArtifactService(serverBaseUrl,user.access_token),
    storageService: externalStorageService,
    eventBusService: externalEventBusService,
    notificationPort: externalNotificationPort,
    showPromptHistory = true,
    showStatistics = true,
    showToolCalls = false,
    showStickyHeader = true,
    autoCreateSession = false,
    showModelSelector = true,
    showSessionInfo = true,
    // v2 is the default: TaskList and sub-agent boxes live in the
    // RunProgressPanel side rail, NOT inline in the chat. Legacy v1
    // surfaces opt back into inline rendering by passing
    // `showTaskList showSubAgents` explicitly.
    showTaskList = false,
    showSubAgents = false,
    // v2 minimal prompt — single sparkle menu + rotated send. v1
    // surfaces pass `compactPrompt={false}` to keep the full Prompt.
    compactPrompt = true,
    // v2 RunProgressPanel owns the connection-status display, so the
    // v1 ConnectionState chip in the chat header is hidden by default.
    // v1 surfaces pass `showConnectionState` explicitly to restore it.
    showConnectionState = false,
    // Hosts that own their own prompt input (e.g. v2 Composer's
    // UnifiedComposerV2) pass `true` to suppress ChatPane's internal
    // Prompt and use ChatPane purely as a transcript.
    hidePromptInput = false,
    friendlyMode = true,
    partRenderers,
    uiTools,
    showPersona,
    formatTimestamp,
    resolvePersona,
    userMessageRenderer,
    onMessage,
    onTurnComplete,
    onTurnOpen,
    onGetWebSocketProvider,
    onConnectionStateChange,
    onSessionCreated,
    onSessionEstablished,
    onError,
    onRenderPrompt,
    onGatewayReady,
    onVisibleQuestionChange,
    onFirstToken,
    children,
}) => {
    const callbacksRef = useRef({ onMessage, onTurnComplete, onConnectionStateChange, onSessionCreated, onSessionEstablished, onError, onGatewayReady, onVisibleQuestionChange, onFirstToken, onTurnOpen });
    callbacksRef.current = { onMessage, onTurnComplete, onConnectionStateChange, onSessionCreated, onSessionEstablished, onError, onGatewayReady, onVisibleQuestionChange, onFirstToken, onTurnOpen };
    const sessionCallsRef = useRef<{ [key: string]: boolean }>({});
    const lastInformationRef = useRef<ConnectionInformation>({
        connectionState: ConnectionState.Disconnected,
        loading: false,
        sesssionActive: false,
        loadingError: "------"
    });

    const theme = useTheme(); 

    const bindPromise = useRef<Promise<void> | null>(null);

    const eventBusService = useMemo(() => {
        return externalEventBusService ?? new EventBusService();
    }, [externalEventBusService]);

    const storageService = useMemo(() => {
        return externalStorageService ?? new LocalStorageService();
    }, [externalStorageService]);

    const defaultNotificationPort: INotificationPort = useMemo(() => ({
        pushNotification: (n) => {
            enqueueSnackbar(n.message, {
                variant: n.type,
                preventDuplicate: true,
                style: {
                    backgroundColor: theme.palette.primary.softBg,
                    color: theme.palette.text.secondary
                }
            });
        },
    }), []);

    const notificationPort = externalNotificationPort ?? defaultNotificationPort;

    const sessionStore = useMemo(() => createAiChatSessionStore(sessionService, onRenderPrompt), []);

    useEffect(() => {
        const subscriptMethod = (state: AiChatState, prevState:AiChatState) => {
            if (callbacksRef.current?.onSessionCreated && (state.session?.id !== prevState.session?.id)) {
                if (state.session) {
                    const key = (state.session?.id ?? "-") + (prevState.session?.id ?? "-");

                    if (callbacksRef.current?.onSessionCreated && state.session)
                        callbacksRef.current.onSessionCreated(state.session)
                }
            }


            if (callbacksRef.current?.onConnectionStateChange && (state.connectionStatus != prevState.connectionStatus || state.loading != prevState.loading || state.loadingError != prevState.loadingError || state.sessionActive != prevState.sessionActive)) {
                const information = {
                    connectionState: state.connectionStatus,
                    loading: state.loading,
                    sesssionActive: state.sessionActive,
                    loadingError: state.loadingError
                }

                if (JSON.stringify(lastInformationRef.current) !== JSON.stringify(information))
                    callbacksRef.current.onConnectionStateChange(information)

                lastInformationRef.current = information
            }
        }
        const subscribe = sessionStore.subscribe(subscriptMethod);
        
        return subscribe;
    }, [sessionStore])

    // Keep a ref to the latest user so the WS getToken callback always reads a fresh JWT after a
    // token refresh (the WebSocketConnecionV2 instance is created once via useMemo).
    const userRef = useRef(user);
    userRef.current = user;
    const getToken = useCallback(() => userRef.current?.access_token, []);

    // Gateway is constructed ONCE per provider lifetime — agent /
    // model changes flow through the gateway's `setAgentContext` /
    // `setModel` live-update methods, NOT through rebuild. This is
    // what makes the agent picker non-destructive: the WS stays
    // open, the chat history survives, and `sendMessageInternal`
    // (which reads `this.config.agentConfig?.id` at send time)
    // picks up the new agent on the very next message.
    //
    // The previous design rebuilt the gateway when an
    // `agentContextKey` derived from `agentContext.agentConfig.id`
    // changed; that forced a fresh WS connection AND lost in-flight
    // session state on every agent switch, which is exactly the
    // "select Customer Dashboard Agent → nothing happens" symptom
    // the host hit when the picker started writing through
    // `useDashboardAppStore.updateDashboardAgent`.
    const agentGatewayService = useMemo(() => {
        let ws: IWebSocketConnection;
        if (onGetWebSocketProvider)
            ws = onGetWebSocketProvider(serverBaseUrl);
        else
            ws = new WebSocketConnecionV2(serverBaseUrl, getToken);

        const gw = new AgentGatewayWSAsync(user, eventBusService, artifactService, serverBaseUrl, agentContext, sessionStore, notificationPort, ws, storageService);

        callbacksRef.current.onGatewayReady?.(gw);
        return gw;
        // Empty deps: the gateway lifetime is the provider lifetime.
        // Agent / model swaps mutate it via the setters below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Propagate live changes to `agentContext` (e.g. admin picks a
    // new agent in the sketch's DatasetParameterizationControls,
    // which writes through `useDashboardAppStore.updateDashboardAgent`
    // → ComposerViewV2 recomputes `dashboardAgentContext` → this
    // prop changes). The setter mutates `gateway.config` in place
    // so the next `sendMessageInternal` ships the new
    // `AgentId` / `CustomerName` / `DataSet` fields. We DON'T tear
    // the WS down or reset the chat history — the session continues
    // with the new agent on its very next prompt.
    const sessionStateManagerService = useMemo(() => {
        return new SessionStateManagerService(
            agentGatewayService,
            sessionService,
            artifactService,
            sessionStore,
            {
                // Stable closure — reads the latest callback off the ref so
                // hosts can swap `onSessionEstablished` without rebuilding
                // the manager (which is constructed once per provider).
                onSessionEstablished: (id) => callbacksRef.current.onSessionEstablished?.(id),
            },
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Propagate `agentContext` prop changes to both the gateway AND the
    // session-state manager. Without the manager push, a heartbeat-driven
    // reconnect would re-open with the stale context captured at the
    // initial bind.
    useEffect(() => {
        agentGatewayService.setAgentContext(agentContext);
        sessionStateManagerService.setAgentContext(agentContext);
    }, [
        agentGatewayService,
        sessionStateManagerService,
        agentContext,
        agentContext.agentConfig?.id,
        agentContext.agentConfig?.name,
        agentContext.customer?.dataSetId,
        agentContext.customer?.name,
    ]);

    // Wire callbacks to EventBus events
    useEffect(() => {
        const handleTurnComplete = async (_interactionId: string, interaction: Interaction) => {
            callbacksRef.current.onTurnComplete?.(interaction);
        };

        const handleMessage = async (interaction: Interaction) => {
            callbacksRef.current.onMessage?.(interaction);
        };

        const handleFirstToken = async (details: { interactionId: string; callId?: string }) => {
            callbacksRef.current.onFirstToken?.(details);
        };

        const handleTurnOpen = async (interactionId: string) => {
            callbacksRef.current.onTurnOpen?.(interactionId);
        };

        eventBusService.gatewayOnTurnComplete(handleTurnComplete);
        eventBusService.gatewayOnMessage(handleMessage);
        eventBusService.gatewayOnTurnOpen(handleTurnOpen);
        eventBusService.gatewayOnFirstToken(handleFirstToken);

        return () => {
            eventBusService.gatewayOffTurnComplete(handleTurnComplete);
            eventBusService.gatewayOffMessage(handleMessage);
            eventBusService.gatewayOffTurnOpen(handleTurnOpen);
            eventBusService.gatewayOffFirstToken(handleFirstToken);
        };
    }, [eventBusService]);

    //69b0fd27062d53f869cb7563
    //69b0fd28062d53f869cb7564

    // Manage SessionStateManagerService lifecycle
    useEffect(() => {
        const resolvedSessionId = (!sessionId || sessionId === '' || sessionId === 'new') ? undefined : sessionId;

        if (!resolvedSessionId && !autoCreateSession) {
            sessionStore.getState().setConnectionStatus('Idle');
            return;
        };

        const mgr = sessionStateManagerService;

        bindPromise.current = bindPromise.current ?? mgr.bindAndOpenLiveSession(agentContext, resolvedSessionId)
        bindPromise.current
            .then(() => {

            })
            .catch((error) => {
                console.error('AiChat: connection error', error);
                callbacksRef.current.onError?.(error instanceof Error ? error : new Error(String(error)));
            })
            .finally(() => {
                bindPromise.current = null;
            })

        return () => {
            mgr.stop().catch(() => { });
        };
    }, [agentGatewayService, sessionStateManagerService, sessionId]);

    const stableOnVisibleQuestionChange = useCallback((question: string | null) => {
        callbacksRef.current.onVisibleQuestionChange?.(question);
    }, []);

    const [localShowToolCalls, setLocalShowToolCalls] = useState(showToolCalls);
    const [localFriendlyMode, setLocalFriendlyMode] = useState(friendlyMode);
    const [currentModel, setCurrentModelState] = useState(agentContext.model);
    const currentModelRef = useRef(agentContext.model);

    const setCurrentModel = useCallback((modelId: string) => {
        const prevVendor = getModelVendor(currentModelRef.current);
        const newVendor = getModelVendor(modelId);

        if (prevVendor !== 'Unknown' && newVendor !== 'Unknown' && prevVendor !== newVendor) {
            notificationPort.pushNotification({
                message: `Atenção migrando de ${prevVendor} para ${newVendor}.`,
                type: 'warning',
            });
        }

        agentGatewayService.setModel(modelId);
        currentModelRef.current = modelId;
        setCurrentModelState(modelId);
    }, [agentGatewayService, notificationPort]);

    const sendMessage = useCallback((text: string, options?: SendMessageOptions) => {
        const { ui, ...gatewayOptions } = options ?? {};
        const clientMessage = new ClientMessage(text, 'text/plain');
        if (ui) {
            // Serialize props to a JSON string (mirrors the server's UserUiContent.args). The
            // gateway carries this to the server as a client-only sibling and injects a matching
            // UserAnswer part on the live interaction so the component renders immediately.
            clientMessage.userUi = {
                name: ui.name,
                args: ui.args === undefined
                    ? undefined
                    : typeof ui.args === 'string' ? ui.args : JSON.stringify(ui.args),
            };
        }
        return agentGatewayService.sendMessage(clientMessage, gatewayOptions);
    }, [agentGatewayService]);       

    const services = useMemo<AiChatContextState>(() => ({
        agentGatewayService,
        sendMessage,
        eventBusService,
        artifactService,
        storageService,
        notificationPort,
        sessionStore,
        showPromptHistory,
        sessionStateManagerService,
        showStatistics,
        showStickyHeader,
        showModelSelector,
        showSessionInfo,
        showTaskList,
        showSubAgents,
        compactPrompt,
        showConnectionState,
        hidePromptInput,
        onRenderPrompt,
        showToolCalls: localShowToolCalls,
        setShowToolCalls: setLocalShowToolCalls,
        friendlyMode: localFriendlyMode,
        setFriendlyMode: setLocalFriendlyMode,
        currentModel,
        setCurrentModel,
        onVisibleQuestionChange: stableOnVisibleQuestionChange,
        partRenderers,
        uiTools,
        showPersona,
        formatTimestamp,
        resolvePersona,
        userMessageRenderer,

    }), [onRenderPrompt, agentGatewayService, sendMessage, eventBusService, artifactService, storageService, notificationPort, sessionStore, showPromptHistory, showStatistics, showModelSelector, showSessionInfo, showTaskList, showSubAgents, compactPrompt, showConnectionState, hidePromptInput, localShowToolCalls, localFriendlyMode, currentModel, setCurrentModel, stableOnVisibleQuestionChange, partRenderers, uiTools, showPersona, formatTimestamp, resolvePersona, userMessageRenderer]);

    return (
        <AiChatServiceContext.Provider value={services}>
            {children}
        </AiChatServiceContext.Provider>
    );
};
